import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  DatePicker,
  Spin,
  InputNumber,
  Row,
  Col,
  Collapse,
  Space,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { RangePickerProps } from 'antd/es/date-picker';
import callApi, { Method } from '../../Api/config';
import {
  getToken,
  getRole,
  deleteToken,
  getAdminName,
  getAdminEmail,
} from '../../service/CookieManager';
import { toastSuccess, toastError, toastInfo } from '../../service/Toast';
import AbstractTabs, { TabActiosProps, TabActiosEventType } from '../Entity/AbstractTabs';
import AbstractLanding from '../Entity/AbstractLanding';
import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import moment, { Moment } from 'moment';
import { green } from '@ant-design/colors';
import DownloadData from '../../components/DownloadData';
import DefaultTableHeader from '../Entity/AbstractLanding/DefaultTableHeader';
import FilterModal from '../../components/FilterModal';
// import { createRoot } from 'react-dom/client';
import { TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import type { TableProps } from 'antd/es/table';
import RefundRequestModal from './RefundRequestModal';
import RefundRequestForm from './RefundRequestForm';
import { RefundFormData } from './RefundRequestForm';
import {
  REFUND_STATUS,
  REFUND_STATUS_OPTIONS,
  REFUND_METHODS,
  REFUND_METHOD_OPTIONS,
  getRefundFeeTypeLabel,
} from '../../constants/AppConstant';
import RefundRequestDetails from '../../components/RefundRequestDetails/RefundRequestDetails';
import { REFUND_REQUEST } from '../../constants/EntityName';
import '../../components/style.scss';
import './styles.scss';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterData {
  date?: {
    startDate: string;
    endDate: string;
  };
  status?: string;
  service?: string;
}

// Define and export RefundStatus type
export type RefundStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REFUNDED';

export interface RefundDetails {
  refundDate: string;
  refundMethod: string;
  refundedBy: string;
  refundedAt: string;
  transactionId?: string;
  refundAmount?: number;
  notes?: string;
  confirmationDocumentUrl?: string;
  confirmationDocumentName?: string;
}

export interface RefundRequest {
  id: string;
  refundRequestId?: string;
  userId: string;
  name: string;
  applicantName?: string;
  email: string;
  applicationType: string;
  refundReason: string;
  paymentMethod: string;
  cardHolderName?: string;
  lastFourDigits?: string;
  venmoId?: string;
  zelleId?: string;
  transactionId?: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  amountPaid: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REFUNDED';
  createdAt: string;
  updatedAt: string;
  orderId: string;
  mobileNumber: string;
  currency: string;
  applicationStage: string;
  paymentDate: string;
  refundType: string;
  approvedBy?: string;
  approvedAt?: string;
  amountApproved?: number;
  approvalRemarks?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  refundDetails?: RefundDetails;
  remarks?: string;
  refundAmount?: number;
  // Internal eligibility confirmation (Ops Manager / Super Admin)
  eligibilityStatus?: 'ELIGIBLE' | 'NOT_ELIGIBLE' | null;
  eligibleAmount?: number | null;
  eligibilityBy?: string | null;
  eligibilityAt?: string | null;
  eligibilityRemarks?: string | null;
  // Fee type selected in the CMS during eligibility (refundType = applicant's choice)
  eligibilityFeeType?: string | null;
  // Application Charges eligibility (Expected Amount reuses eligibleAmount)
  eligibilityExpectedDate?: string | null;
  eligibilityPaymentVoucherId?: string | null;
  // Accountant payment reconciliation (Application Charges, after approval)
  reconciliationSuspenseVoucherId?: string | null;
  reconciliationReceivedAmount?: number | null;
  reconciliationReceivedDate?: string | null;
  reconciliationReceivedFrom?: string | null;
  reconciliationReceivedIn?: string | null;
  reconciliationBy?: string | null;
  reconciliationAt?: string | null;
}

const REFUND_ENTITY = '/v1/refund';

const RefundRequests: React.FC = () => {
  const [refunds, setRefunds] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRefund, setSelectedRefund] = useState<RefundRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [rejectReason, setRejectReason] = useState<string>('');
  const [rejectedBy, setRejectedBy] = useState('');
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSearch, setFilterSearch] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const adminRole = getRole();
  const [activeKey, setActiveKey] = useState<string>('0');
  const [content, setContent] = useState<any[]>([]);
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>();
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundDetails, setRefundDetails] = useState<RefundDetails>({
    refundDate: '',
    refundAmount: 0,
    refundMethod: '',
    refundedBy: '',
    refundedAt: '',
    notes: '',
  });
  const [refundForm] = Form.useForm();
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedRefundRequest, setSelectedRefundRequest] = useState<RefundRequest | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('PENDING');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<any>(null);
  const [refundStatus, setRefundStatus] = useState<string>('PENDING');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const defaultFormValue = {};
  const headerName = 'Refund Request';

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<RefundRequest> | SorterResult<RefundRequest>[]
  ) => {
    const newPage = pagination.current || 1;
    const newPageSize = pagination.pageSize || 10;

    setCurrentPage(newPage);
    setPageSize(newPageSize);

    if (Array.isArray(sorter)) {
      setSortField((sorter[0]?.field as string) || '');
      setSortOrder((sorter[0]?.order as string) || '');
    } else {
      setSortField((sorter.field as string) || '');
      setSortOrder((sorter.order as string) || '');
    }

    fetchRefunds(newPage, newPageSize);
  };

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  const fetchRefunds = useCallback(
    async (page = currentPage, size = pageSize) => {
      try {
        setLoading(true);
        // Construct query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('page', String(page));
        queryParams.append('limit', String(size));

        // Add search parameters
        if (searchQuery) {
          const trimmedSearch = searchQuery.trim();
          if (trimmedSearch) {
            queryParams.append('search', trimmedSearch);
            queryParams.append(
              'searchFields',
              'name,applicantName,email,orderId,mobileNumber,refundRequestId'
            );
          }
        }

        // Add filters from DefaultTableHeader
        if (filterSearch) {
          Object.keys(filterSearch).forEach(key => {
            const value = filterSearch[key];
            if (value !== undefined && value !== null && value !== '') {
              queryParams.append(key, String(value));
            }
          });
        }
        // Add manual status filter if not already covered by filterSearch
        if (filterStatus && filterStatus !== 'all' && (!filterSearch || !filterSearch.status)) {
          queryParams.append('status', filterStatus);
        }

        const url = `${REFUND_ENTITY}?${queryParams.toString()}`;

        const response = await callApi(url, 'GET', null, null, {
          Authorization: `Bearer ${getToken()}`,
        });

        if (response?.data?.isSuccess === 1 && response?.data?.data?.results) {
          const mappedRefunds = response.data.data.results.map((item: any) => ({
            id: item.id,
            userId: item.userId || item.id,
            name: item.applicantName || item.name,
            email: item.email,
            applicationType: item.applicationType,
            refundReason: item.refundReason || item.reason,
            paymentMethod: item.paymentMethod,
            cardHolderName: item.cardHolderName,
            lastFourDigits: item.lastFourDigits,
            venmoId:
              item.venmoId ||
              item.venmo_id ||
              item.venmo ||
              item.paymentDetails?.venmo ||
              item.paymentDetails?.venmoId,
            zelleId:
              item.zelleId ||
              item.zelle_id ||
              item.zelle ||
              item.paymentDetails?.zelle ||
              item.paymentDetails?.zelleId,
            transactionId:
              item.transactionId ||
              item.stripePaymentIntentId ||
              item.stripeChargeId ||
              item.paymentTransactionId,
            stripePaymentIntentId: item.stripePaymentIntentId,
            stripeChargeId: item.stripeChargeId,
            amountPaid: parseFloat(item.amount),
            status: item.status,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            orderId: item.orderId,
            mobileNumber: item.mobileNumber,
            currency: item.currency || 'USD',
            applicationStage: '',
            paymentDate: item.paymentDate,
            refundType: item.refundType || '',
            eligibilityFeeType: item.eligibilityFeeType || null,
            approvedBy: item.approvedBy,
            approvedAt: item.approvedAt || null,
            amountApproved:
              item.amountApproved != null ? parseFloat(item.amountApproved) : undefined,
            approvalRemarks: item.approvalRemarks || '',
            rejectedBy: item.rejectedBy,
            rejectedAt: item.rejectedAt,
            rejectionReason: item.rejectionReason || '',
            remarks: item.remarks,
            refundAmount: item.refundAmount != null ? parseFloat(item.refundAmount) : undefined,
            refundDetails: item.refundDetails
              ? {
                  refundDate: item.refundDetails.refundDate,
                  refundAmount:
                    item.refundDetails.refundAmount != null
                      ? parseFloat(item.refundDetails.refundAmount)
                      : item.refundAmount != null
                        ? parseFloat(item.refundAmount)
                        : undefined,
                  refundMethod: item.refundDetails.refundMethod,
                  refundedBy: item.refundDetails.refundedBy || item.refundedBy,
                  refundedAt: item.refundDetails.refundedAt || item.refundedAt,
                  transactionId: item.refundDetails.transactionId,
                  notes: item.refundDetails.notes || '',
                  confirmationDocumentUrl: item.refundDetails.confirmationDocumentUrl,
                  confirmationDocumentName: item.refundDetails.confirmationDocumentName,
                }
              : item.refundedBy
                ? {
                    refundDate: item.refundedAt,
                    refundAmount: parseFloat(
                      item.refundAmount || item.amountApproved || item.amount
                    ),
                    refundMethod: '',
                    refundedBy: item.refundedBy,
                    refundedAt: item.refundedAt,
                    notes: '',
                  }
                : undefined,
            refundRequestId: item.refundRequestId,
          }));

          setRefunds(mappedRefunds);
          setTotalCount(response.data.data.totalCount || 0);
        } else {
          setRefunds([]);
          setTotalCount(0);
        }
      } catch (err: any) {
        console.error('Error fetching refunds:', err);
        setError(err.message || 'Failed to fetch refund requests');
        setRefunds([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, searchQuery, filterSearch, filterStatus]
  );

  // Initial fetch
  useEffect(() => {
    fetchRefunds();
    // Cleanup function
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [fetchRefunds]);

  const handleFilterChange = (value: string) => {
    setFilterStatus(value);
    setCurrentPage(1);
    fetchRefunds(1, pageSize);
  };

  const handleExportExcel = () => {
    // Implementation
  };

  const handleExportPDF = () => {
    // Implementation
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleTotal = (total: number) => {
    setTotalCount(total);
  };

  const fetchRefundTransactionDetails = async (refundId: string) => {
    try {
      console.log('Fetching transaction details for refund:', refundId);
      const response = await callApi(`${REFUND_ENTITY}/details/${refundId}`, 'GET');
      console.log('Raw API Response:', JSON.stringify(response, null, 2));

      if (!response?.data?.isSuccess) {
        console.error('API call was not successful:', response?.data);
        return null;
      }

      // Get transaction details from the response
      const transactionDetails = response.data.data.transactionDetails || [];
      console.log('Transaction Details:', transactionDetails);

      // Return the transaction details array
      return transactionDetails;
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      return null;
    }
  };

  const checkExistingRefundRequest = async (orderId: string) => {
    try {
      const response = await callApi(`${REFUND_ENTITY}/check/${orderId}`, 'GET');
      return response?.data?.data;
    } catch (error) {
      console.error('Error checking existing refund request:', error);
      return null;
    }
  };

  const handleOpenRefundForm = async (orderId: string) => {
    try {
      // Check for existing refund request
      const existingRequest = await checkExistingRefundRequest(orderId);

      if (existingRequest) {
        // If there's an existing request, show it instead of creating a new one
        setSelectedRefundRequest(existingRequest);
        toastInfo('A refund request already exists for this order.');

        // Navigate to the existing refund request
        const element = document.getElementById(`refund-row-${existingRequest.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Highlight the row briefly
          element.style.backgroundColor = '#ffd591';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      } else {
        // If no existing request, open the form
        setIsFormModalOpen(true);
      }
    } catch (error) {
      console.error('Error checking refund request:', error);
      toastError('Error checking existing refund request');
    }
  };

  const handleRefundFormSubmit = async (formData: RefundFormData) => {
    try {
      // Check for existing refund request again before submitting
      const existingRequest = await checkExistingRefundRequest(formData.orderId);

      if (existingRequest) {
        toastInfo('A refund request already exists for this order.');
        setIsFormModalOpen(false);

        // Navigate to the existing refund request
        const element = document.getElementById(`refund-row-${existingRequest.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          element.style.backgroundColor = '#ffd591';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
        return;
      }

      // If no existing request, proceed with creating new one
      const response = await callApi(REFUND_ENTITY, 'POST', formData);

      if (response?.data?.isSuccess) {
        toastSuccess('Refund request created successfully');
        setIsFormModalOpen(false);
        fetchRefunds(); // Refresh the list
      } else {
        toastError(response?.data?.message || 'Error creating refund request');
      }
    } catch (error) {
      console.error('Error submitting refund request:', error);
      toastError('Error creating refund request');
    }
  };

  const getAvailableStatusOptions = (currentStatus: string) => {
    if (currentStatus === 'PENDING') {
      return [
        { value: 'PENDING', name: 'Pending' },
        { value: 'APPROVED', name: 'Approve' },
        { value: 'REJECTED', name: 'Reject' },
      ];
    }
    // For non-pending statuses, only show the current status
    return [
      {
        value: currentStatus,
        name: currentStatus.charAt(0) + currentStatus.slice(1).toLowerCase(),
      },
    ];
  };

  const handleStatusChange = (value: string) => {
    console.log('Status changed to:', value);
    setSelectedStatus(value);

    // Reset rejection fields if changing from REJECTED to another status
    if (value !== 'REJECTED') {
      setRejectReason('');
      setRejectedBy('');
    }
  };

  const handleStatusUpdate = async (refundId: string, newStatus: RefundStatus, reason?: string) => {
    try {
      if (adminRole !== 'Super Admin') {
        toastError('You do not have permission to update refund status');
        return;
      }

      const loggedInUserName = getAdminName() || getAdminEmail() || 'Unknown User';
      const currentTime = new Date().toISOString();
      const endpoint = `${REFUND_ENTITY}/${refundId}`;
      const method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'PATCH';
      let requestBody: any = {
        status: newStatus,
        remarks: reason || undefined,
        updatedBy: loggedInUserName,
        updatedAt: currentTime,
      };

      if (newStatus === 'REJECTED') {
        requestBody = {
          ...requestBody,
          rejectedBy: loggedInUserName,
          rejectedAt: currentTime,
          rejectionReason: reason,
        };
      } else if (newStatus === 'APPROVED') {
        requestBody = {
          ...requestBody,
          approvedBy: loggedInUserName,
          approvedAt: currentTime,
        };
      }

      const response = await callApi(endpoint, method, requestBody);

      if (response?.data?.success || response?.data?.isSuccess) {
        toastSuccess(`Refund request ${newStatus.toLowerCase()} successfully`);

        // Find the current refund and create updated version
        const currentRefund = refunds.find(r => r.id === refundId);
        if (!currentRefund) {
          toastError('Refund request not found');
          return;
        }

        // Create the updated refund object with all required fields
        const updatedRefund: RefundRequest = {
          ...currentRefund,
          status: newStatus,
          ...(newStatus === 'REJECTED' && {
            rejectedBy: rejectedBy || loggedInUserName,
            rejectedAt: currentTime,
            rejectionReason: reason,
          }),
          ...(newStatus === 'APPROVED' && {
            approvedBy: loggedInUserName,
            approvedAt: currentTime,
          }),
        };

        // Update the refunds array immediately
        setRefunds(prevRefunds =>
          prevRefunds.map(refund => (refund.id === refundId ? updatedRefund : refund))
        );

        // Update the content if detail view is open
        const contentIndex = content.findIndex(item => item.key === `${refundId}-details`);
        if (contentIndex !== -1) {
          const newContent = [...content];
          newContent[contentIndex] = {
            ...newContent[contentIndex],
            content: (
              <RefundRequestDetails
                refund={updatedRefund}
                onStatusChange={(updatedRefund: RefundRequest) => {
                  setRefunds(prev =>
                    prev.map(r => (r.id === updatedRefund.id ? updatedRefund : r))
                  );
                }}
                loading={loading}
              />
            ),
          };
          setContent(newContent);
        }

        // Update the selected refund if it's the current one
        if (selectedRefund?.id === refundId) {
          setSelectedRefund(updatedRefund);
        }

        // Update the selected refund request if it's the current one
        if (selectedRefundRequest?.id === refundId) {
          setSelectedRefundRequest(updatedRefund);
        }

        // Reset form states
        setRejectReason('');
        setRejectedBy('');
        setSelectedStatus('PENDING');
      } else {
        toastError(response?.data?.message || 'Failed to update refund status');
      }
    } catch (err: any) {
      console.error('Error in status update:', err);
      toastError(err.response?.data?.message || 'Failed to update refund status');
    }
  };

  const handleReject = (refund: RefundRequest) => {
    setSelectedRefund(refund);
    setSelectedStatus('REJECTED');
    setIsRejectModalOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (selectedRefund && rejectReason && rejectedBy) {
      await handleStatusUpdate(selectedRefund.id, 'REJECTED', rejectReason);
      setIsRejectModalOpen(false);
      setRejectReason('');
      setRejectedBy('');
    } else {
      toastError('Please provide both rejection reason and rejected by');
    }
  };

  const handleMarkAsRefunded = (record: RefundRequest) => {
    // Use detail view (approval + refunded sections) so confirmation upload is enforced
    onAdd({
      id: record.id,
      name: record.name || 'Refund Request Details',
    });
  };

  const handleRefundSubmit = async () => {
    toastError(
      'Please process refunds from the Refund Request details tab with a confirmation document attached.'
    );
    setIsRefundModalOpen(false);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({
      current: page,
      pageSize: pageSize,
    });
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const onAdd = (argument1: any) => {
    const record = refunds.find(r => r.id === argument1.id);
    if (!record) return;

    // Check if tab already exists
    const existingTab = content.find(tab => tab.key === argument1.id);
    if (existingTab) {
      // If tab exists, just switch to it
      setActiveKey(String(argument1.id));
      return;
    }

    setSelectedStatus('APPROVED');
    setSelectedRefund(record);
    setRejectReason('');

    const newObj = {
      key: argument1.id,
      tabTitle: `${record.refundRequestId || record.id}`,
      content: renderDetailView(record),
    };

    setContent(prev => [...prev, newObj]);
    setActiveKey(String(argument1.id));
  };

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && fetchRefunds(pagination.current, pagination.pageSize);
  };

  const onTabClick = (key: string) => {
    // If key is '0' (main list) or tab exists, switch to it
    if (key === '0' || content.some(tab => tab.key === key)) {
      setActiveKey(key);
    } else {
      // If tab doesn't exist, go back to main list
      setActiveKey('0');
    }
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      onTabClick('0');
      setContent(content => content.filter(item => String(item.key) !== targetKey));
    }
  };

  const onSuccessful = (id?: string) => {
    if (id === 'create') {
      setShowFormComponent(false);
    }
    onTabClick('0');
    fetchRefunds(pagination.current, pagination.pageSize);
  };

  const handleRefundClick = (record: RefundRequest) => {
    setSelectedRefund(record);
    setShowFormComponent(true);
  };

  const handleRefundStatusChange = (value: string) => {
    setRefundStatus(value);
  };

  const collapseStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #f0f0f0',
    borderRadius: '4px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  };

  const panelStyle = {
    marginBottom: 0,
    borderRadius: 0,
    border: 'none',
    backgroundColor: '#ffffff',
  };

  const renderStatusSection = (record: RefundRequest) => {
    return (
      <div style={{ padding: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#8c8c8c' }}>Current Status: </span>
              <span
                style={{
                  marginLeft: '8px',
                  color: getStatusColor(record.status),
                  fontWeight: 500,
                  textTransform: 'uppercase',
                }}
              >
                {record.status}
              </span>
            </div>

            <div style={{ marginBottom: '8px', color: '#8c8c8c' }}>
              Update Status <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Row gutter={[24, 24]}>
              <Col span={8}>
                <Select
                  style={{ width: '100%' }}
                  dropdownClassName="select-checkbox-dropdown"
                  value={selectedStatus}
                  onChange={value => setSelectedStatus(value)}
                  options={REFUND_STATUS_OPTIONS}
                />
              </Col>

              {selectedStatus === 'REJECTED' && (
                <Col span={16}>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <div style={{ marginBottom: '8px', color: '#8c8c8c' }}>
                        Rejection Reason <span style={{ color: '#ff4d4f' }}>*</span>
                      </div>
                      <Input.TextArea
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="Please provide a detailed reason for rejection"
                        rows={4}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: '8px', color: '#8c8c8c' }}>
                        Rejected By <span style={{ color: '#ff4d4f' }}>*</span>
                      </div>
                      <Input
                        value={rejectedBy}
                        onChange={e => setRejectedBy(e.target.value)}
                        placeholder="Enter your name"
                      />
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>

            <Row style={{ marginTop: '24px' }}>
              <Col span={24}>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      if (selectedStatus === 'REJECTED') {
                        if (!rejectReason) {
                          toastError('Please enter rejection reason');
                          return;
                        }
                        if (!rejectedBy) {
                          toastError('Please enter who is rejecting this request');
                          return;
                        }
                      }
                      handleStatusUpdate(
                        record.id,
                        selectedStatus as RefundStatus,
                        selectedStatus === 'REJECTED' ? rejectReason : undefined
                      );
                    }}
                    style={{
                      backgroundColor:
                        selectedStatus === 'APPROVED'
                          ? '#52c41a'
                          : selectedStatus === 'REJECTED'
                            ? '#f5222d'
                            : '#1890ff',
                      borderColor:
                        selectedStatus === 'APPROVED'
                          ? '#52c41a'
                          : selectedStatus === 'REJECTED'
                            ? '#f5222d'
                            : '#1890ff',
                    }}
                    disabled={adminRole === 'Accountant' || adminRole === 'Ops Manager'}
                  >
                    {selectedStatus === 'APPROVED'
                      ? 'Approve Request'
                      : selectedStatus === 'REJECTED'
                        ? 'Reject Request'
                        : 'Update Status'}
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedStatus(record.status);
                      setRejectReason('');
                      setRejectedBy('');
                    }}
                  >
                    Reset
                  </Button>
                </Space>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  };

  const renderDetailView = (record: RefundRequest) => {
    return (
      <div className="page-content">
        <RefundRequestDetails
          refund={record}
          onStatusChange={(updatedRefund: RefundRequest) => {
            setRefunds(prev => prev.map(r => (r.id === updatedRefund.id ? updatedRefund : r)));
          }}
          loading={loading}
        />
      </div>
    );
  };

  // Add this useEffect to initialize selectedStatus when a record is selected
  useEffect(() => {
    if (selectedRefund) {
      setSelectedStatus(selectedRefund.status);
    }
  }, [selectedRefund]);

  const renderContent = () => {
    return (
      <div>
        <DefaultTableHeader
          headerName={headerName}
          totalCount={totalCount}
          setSearchQuery={handleSearch}
          setPhoneSearchQuery={() => {}}
          setCitySearchQuery={() => {}}
          setStateSearchQuery={() => {}}
          onAdd={() => {}}
          entityName={REFUND_REQUEST}
          showDownload={false}
          showFilter={true}
          filterQuery={filters => {
            setFilterSearch(filters);
            setCurrentPage(1);
          }}
          disableAdd={true}
          reportRedirectPath="/documitra-reports"
        />
        <div style={{ position: 'relative', minHeight: '200px' }}>
          {error ? (
            <div
              style={{
                textAlign: 'center',
                padding: '20px',
                color: 'red',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <Button type="primary" onClick={() => fetchRefunds(currentPage, pageSize)}>
                Retry
              </Button>
            </div>
          ) : (
            <Table<RefundRequest>
              columns={columns}
              dataSource={refunds}
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (total: number) => `Total ${total} items`,
                onChange: handlePageChange,
              }}
              onChange={handleTableChange}
              rowKey="id"
              scroll={{ x: 'max-content' }}
              rowClassName={record =>
                selectedRefundRequest?.id === record.id ? 'highlighted-row' : ''
              }
            />
          )}
        </div>
      </div>
    );
  };

  const columns: ColumnsType<RefundRequest> = [
    {
      title: <div style={{ textAlign: 'left' }}>Refund ID</div>,
      dataIndex: 'refundRequestId',
      key: 'refundRequestId',
      width: 140,
      ellipsis: true,
      className: 'table-cell',
      render: (_text: string, record: RefundRequest) => {
        const displayId = record.refundRequestId || record.id;
        return (
          <div style={{ textAlign: 'left' }}>
            <span
              onClick={() =>
                onAdd({
                  id: record.id,
                  name: record.name || 'Refund Request Details',
                })
              }
              style={{
                color: '#000000',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {displayId}
            </span>
          </div>
        );
      },
    },
    {
      title: <div style={{ textAlign: 'left' }}>Name</div>,
      dataIndex: 'name',
      key: 'name',
      width: 220,
      ellipsis: true,
      sorter: (a: RefundRequest, b: RefundRequest) => a.name.localeCompare(b.name),
      className: 'table-cell',
      render: (text: string, record: RefundRequest) => (
        <div style={{ textAlign: 'left' }}>
          <span
            onClick={() =>
              onAdd({
                id: record.id,
                name: record.name || 'Refund Request Details',
              })
            }
            style={{
              color: '#000000',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {text}
          </span>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: 'left' }}>Order Id </div>,
      dataIndex: 'orderId',
      key: 'orderId',
      width: 140,
      ellipsis: true,
      className: 'table-cell',
      render: (text: string) => <div style={{ textAlign: 'left' }}>{text}</div>,
    },
    {
      title: <div style={{ textAlign: 'left' }}>Fee Type</div>,
      dataIndex: 'refundType',
      key: 'refundType',
      width: 160,
      className: 'table-cell',
      // Confirmed (CMS-selected) fee type when set, else the applicant's request.
      render: (refundType: string, record: RefundRequest) => (
        <div style={{ textAlign: 'left' }}>
          {getRefundFeeTypeLabel(record.eligibilityFeeType || refundType)}
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: 'left' }}>Email</div>,
      dataIndex: 'email',
      key: 'email',
      width: 220,
      ellipsis: true,
      className: 'table-cell',
      render: (text: string) => <div style={{ textAlign: 'left' }}>{text}</div>,
    },
    {
      title: <div style={{ textAlign: 'left' }}>Mobile</div>,
      dataIndex: 'mobileNumber',
      key: 'mobileNumber',
      width: 170,
      className: 'table-cell',
      render: (text: string) => <div style={{ textAlign: 'left' }}>{text}</div>,
    },
    {
      title: <div style={{ textAlign: 'left' }}>Service Name</div>,
      dataIndex: 'applicationType',
      key: 'applicationType',
      width: 160,
      ellipsis: true,
      className: 'table-cell',
      render: (text: string) => (
        <div style={{ textAlign: 'left' }}>
          {text && text.toUpperCase() !== 'N/A' ? text : '--'}
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: 'left' }}>Reason for Refund</div>,
      dataIndex: 'refundReason',
      key: 'refundReason',
      width: 180,
      ellipsis: true,
      className: 'table-cell',
      render: (text: string) => <div style={{ textAlign: 'left' }}>{text}</div>,
    },
    {
      title: <div style={{ textAlign: 'left' }}>Payment Date</div>,
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 140,
      className: 'table-cell',
      render: (date: string) => (
        <div style={{ textAlign: 'left' }}>{date ? moment(date).format('DD/MM/YYYY') : '--'}</div>
      ),
    },
    {
      title: <div style={{ textAlign: 'left' }}>Payment Method</div>,
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 170,
      className: 'table-cell',
      render: (text: string) => <div style={{ textAlign: 'left' }}>{text}</div>,
    },
    {
      title: <div style={{ textAlign: 'left' }}>Amount</div>,
      dataIndex: 'amountPaid',
      key: 'amountPaid',
      width: 150,
      className: 'table-cell',
      render: (amount: number, record: RefundRequest) => (
        <div style={{ textAlign: 'left' }}>{`${record.currency} ${amount.toFixed(2)}`}</div>
      ),
      sorter: (a: RefundRequest, b: RefundRequest) => (a.amountPaid || 0) - (b.amountPaid || 0),
    },
    {
      title: <div style={{ textAlign: 'left' }}>Request Date</div>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 220,
      className: 'table-cell',
      render: (date: string) => (
        <div style={{ textAlign: 'left' }}>{moment(date).format('DD/MM/YYYY, HH:mm:ss')}</div>
      ),
      sorter: (a: RefundRequest, b: RefundRequest) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: <div style={{ textAlign: 'left' }}>Status</div>,
      dataIndex: 'status',
      key: 'status',
      width: 110,
      className: 'table-cell',
      render: (status: string) => {
        const statusColor = getStatusColor(status);
        return (
          <div style={{ textAlign: 'left' }}>
            <span
              style={{
                color: statusColor,
                fontWeight: 'bold',
                fontSize: '12px',
                textTransform: 'uppercase',
              }}
            >
              {status}
            </span>
          </div>
        );
      },
    },
  ];

  const handleViewDetails = (record: RefundRequest) => {
    setSelectedRefundRequest(record);
    // Add this record to content if it's not already there
    const existingContentIndex = content.findIndex(item => item.key === `${record.id}-details`);

    if (existingContentIndex === -1) {
      const newContent = {
        key: `${record.id}-details`,
        tab: `Refund Details - ${record.orderId}`,
        content: (
          <RefundRequestDetails
            refund={record}
            onStatusChange={(updatedRefund: RefundRequest) => {
              setRefunds(prev => prev.map(r => (r.id === updatedRefund.id ? updatedRefund : r)));
            }}
            loading={loading}
          />
        ),
      };
      setContent(prev => [...prev, newContent]);
    } else {
      // If the content already exists, update it with the latest data
      const newContent = [...content];
      newContent[existingContentIndex] = {
        ...newContent[existingContentIndex],
        content: (
          <RefundRequestDetails
            refund={record}
            onStatusChange={(updatedRefund: RefundRequest) => {
              setRefunds(prev => prev.map(r => (r.id === updatedRefund.id ? updatedRefund : r)));
            }}
            loading={loading}
          />
        ),
      };
      setContent(newContent);
    }
    setActiveKey(`${record.id}-details`);
  };

  const handleAddRefund = () => {
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = async (data: RefundFormData) => {
    try {
      setLoading(true);
      const response = await callApi(REFUND_ENTITY, 'POST', {
        ...data,
        status: 'PENDING',
        createdAt: new Date().toISOString(),
      });

      if (response?.data?.isSuccess) {
        toastSuccess('Refund request submitted successfully');
        fetchRefunds(currentPage, pageSize);
      } else {
        toastError(response?.data?.message || 'Failed to submit refund request');
      }
    } catch (error) {
      toastError('Error submitting refund request');
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setIsFormModalOpen(false);
    }
  };

  // Add Modal for Rejection
  const RejectModal = () => (
    <Modal
      title="Reject Refund Request"
      open={isRejectModalOpen}
      onOk={handleRejectSubmit}
      onCancel={() => {
        setIsRejectModalOpen(false);
        setRejectReason('');
        setRejectedBy('');
      }}
      okText="Submit"
      okButtonProps={{
        style: { backgroundColor: '#f5222d', borderColor: '#f5222d' },
        disabled: !rejectReason || !rejectedBy,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <div style={{ marginBottom: '8px' }}>Rejection Reason</div>
          <Input.TextArea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejection"
            rows={4}
          />
        </div>
        <div>
          <div style={{ marginBottom: '8px' }}>Rejected By</div>
          <Input
            value={rejectedBy}
            onChange={e => setRejectedBy(e.target.value)}
            placeholder="Enter your name"
          />
        </div>
      </div>
    </Modal>
  );

  // Add Modal for Refund Details
  const RefundModal = () => (
    <Modal
      title="Mark as Refunded"
      open={isRefundModalOpen}
      onOk={handleRefundSubmit}
      onCancel={() => {
        setIsRefundModalOpen(false);
        refundForm.resetFields();
      }}
      okText="Submit"
      okButtonProps={{
        style: { backgroundColor: '#0b53d9', borderColor: '#0b53d9' },
        disabled:
          !refundForm.isFieldsTouched(true) ||
          !!refundForm.getFieldsError().filter(({ errors }) => errors.length).length,
      }}
    >
      <Form form={refundForm} layout="vertical">
        <Form.Item
          name="refundDate"
          label="Refund Date"
          rules={[{ required: true, message: 'Please select refund date' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="refundAmount"
          label="Refund Amount"
          rules={[{ required: true, message: 'Please enter refund amount' }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value!.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>
        <Form.Item
          name="refundMethod"
          label="Refund Method"
          rules={[{ required: true, message: 'Please select refund method' }]}
        >
          <Select dropdownClassName="select-checkbox-dropdown">
            {REFUND_METHOD_OPTIONS.map(option => (
              <Option key={option.value} value={option.value}>
                {option.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="refundedBy"
          label="Refunded By"
          rules={[{ required: true, message: 'Please enter who processed the refund' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );

  // Add these styles at the top of your file or in a separate CSS file
  const styles = `
        .page-content {
            padding: 24px;
        }

        .section-wrapper {
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            margin-bottom: 24px;
        }

        .section-header {
            padding: 16px 24px;
            border-bottom: 1px solid #f0f0f0;
        }

        .section-header h2 {
            margin: 0;
            color: #1890ff;
            font-size: 16px;
            font-weight: 500;
        }

        .section-content {
            padding: 24px;
        }

        .form-label {
            color: #666;
            font-size: 14px;
            font-weight: 500;
        }

        .form-input,
        .form-select,
        .form-textarea {
            width: 100%;
        }

        .form-input[readonly],
        .form-textarea[readonly] {
            background-color: #f5f5f5;
            cursor: default;
        }

        .status-button {
            min-width: 120px;
        }

        .status-button.approve {
            background-color: #52c41a;
            border-color: #52c41a;
        }

        .status-button.reject {
            background-color: #f5222d;
            border-color: #f5222d;
        }

        .highlighted-row {
            background-color: #ffd591;
            transition: background-color 0.3s ease;
        }

        .highlighted-row:hover {
            background-color: #ffc069;
        }
    `;

  // Add this near the top of your component
  useEffect(() => {
    // Add styles to document
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);

    // Cleanup
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Add this near other useEffect hooks
  useEffect(() => {
    // Cleanup function to reset tab state when component unmounts
    return () => {
      setContent([]);
      setActiveKey('0');
      setSelectedRefund(null);
      setSelectedStatus('PENDING');
      setRejectReason('');
      setRejectedBy('');
    };
  }, []);

  return (
    <div className="refund-requests">
      <AbstractTabs
        activeKey={activeKey}
        entityName="Refund Requests"
        onTabClick={onTabClick}
        content={content}
        updateFormVisibility={updateFormVisibility}
        children={renderContent()}
      />
      <RejectModal />
      <RefundModal />

      <RefundRequestForm
        open={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleRefundFormSubmit}
      />

      {selectedRefundRequest && (
        <RefundRequestModal
          open={!!selectedRefundRequest}
          onClose={() => setSelectedRefundRequest(null)}
          refundRequest={selectedRefundRequest}
          onStatusUpdate={(status: RefundStatus) =>
            handleStatusUpdate(selectedRefundRequest.userId, status)
          }
        />
      )}
    </div>
  );
};

export default RefundRequests;

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    }}
  >
    <span
      style={{
        color: '#666',
        fontSize: '12px',
        fontWeight: 500,
      }}
    >
      {label}
    </span>
    <span
      style={{
        color: '#000',
        fontSize: '14px',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </span>
  </div>
);

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return '#faad14';
    case 'APPROVED':
      return '#52c41a';
    case 'REJECTED':
      return '#f5222d';
    case 'REFUNDED':
      return '#1890ff';
    default:
      return '#d9d9d9';
  }
};
